import React from "react";
// UI Framework
import { TableCell, TableRow } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
// Common Components
import { TextInput, Loading, Table, DynamicDropdown } from "src/Components";
// Services
import {
  addSymbol,
  getExcludedCalculationList,
  deleteExcludedCalculationSymbolRecord,
  getSymbolDropdown,
} from "src/Requests/imePortfolio";
// Utilities
import { packetErrorHandler } from "src/Utils/requestUtils";
import { addComma, removeComma } from "src/helper";
// Types
import {
  IUserPositionSummarySearchParams,
  ExcludedCalculationSymbolSearchParams,
  ExcludedCalculationSymbolTableReport,
} from "src/Interfaces/imePortfolio";

interface IProps {
  searchParams: IUserPositionSummarySearchParams;
  setRefresh: (flag: boolean) => void;
}

const ExcludedCalculationSymbolModal = ({
  searchParams,
  setRefresh,
}: IProps) => {
  const [loading, setLoading] = React.useState(false);
  const [baseConfig, setBaseConfig] = React.useState<
    ExcludedCalculationSymbolTableReport[]
  >([]);
  const [searchParam, setSearchParam] = React.useState({
    userId: searchParams.userId,
  } as ExcludedCalculationSymbolSearchParams);

  const fetchExcludedCalculationTable = React.useCallback(() => {
    setLoading(true);
    getExcludedCalculationList(searchParams.userId)
      .then((resp) => {
        setBaseConfig(resp);
        setSearchParam({} as ExcludedCalculationSymbolSearchParams);
      })
      .catch(packetErrorHandler)
      .finally(() => setLoading(false));
  }, [searchParams.userId]);

  React.useEffect(() => {
    setSearchParam((prev) => ({
      ...prev,
      userId: searchParams.userId,
    }));
    fetchExcludedCalculationTable();
  }, [fetchExcludedCalculationTable, searchParams.userId]);

  const changeHandler = (name: string, value: any) => {
    setSearchParam((prev) => ({
      ...prev,
      [name]: removeComma(value),
      userId: searchParams.userId,
    }));
  };

  return (
    <div className="w-full px-4">
      <div>
        {loading ? (
          <div className="w-full h-full flex justify-center items-center">
            <Loading className="text-blue-500" />
          </div>
        ) : (
          <div className="border-2 border-blue-100 w-full">
            <Table
              header={<div />}
              body={
                <>
                  <TableRow>
                    <TableCell>
                      <div className="text-sm font-bold w-44">نماد</div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm font-bold">تعداد</div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm font-bold">عملیات</div>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <DynamicDropdown
                        dropdown={{
                          name: "symbol",
                          placeholder: "نماد",
                        }}
                        value={searchParam.symbol}
                        getData={getSymbolDropdown}
                        onChange={changeHandler}
                      />
                    </TableCell>
                    <TableCell>
                      <TextInput
                        label="تعداد"
                        name="count"
                        onChange={changeHandler}
                        value={addComma(searchParam?.count)}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <div
                        className={`mx-10 flex items-center justify-center  transition-all w-5 h-5 rounded-full   ${searchParams.userId ? "hover:bg-green-600 bg-green-700 cursor-pointer" : "bg-gray-500 cursor-not-allowed"}`}
                        onClick={() => {
                          addSymbol({
                            ...searchParam,
                            userId: searchParams.userId,
                          }).then(() => {
                            fetchExcludedCalculationTable();
                            setRefresh(true);
                          });
                        }}
                      >
                        <AddIcon
                          fontSize="small"
                          className={`text-white transition-all`}
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                  {baseConfig?.map((item) => (
                    <TableRow>
                      <TableCell>
                        <div className="w-full text-center">{item.symbol}</div>
                      </TableCell>
                      <TableCell>
                        <div className="w-full text-center">
                          {addComma(item.count)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div
                          className="hover:bg-red-100 transition-all rounded p-1"
                          onClick={() => {
                            deleteExcludedCalculationSymbolRecord(item.id).then(
                              () => {
                                fetchExcludedCalculationTable();
                                setRefresh(true);
                              },
                            );
                          }}
                        >
                          <DeleteIcon
                            fontSize="small"
                            className={`cursor-pointer text-red-600 transition-all`}
                          />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </>
              }
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ExcludedCalculationSymbolModal;
